/**
 * Lightweight accessibility helpers for shared UI components.
 * Keep changes minimal — do not alter business logic.
 */

let idCounter = 0;

/** Stable unique id for associating labels/errors with controls. */
export function getA11yId(prefix = "a11y"): string {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

/** Build aria-describedby from optional error/help ids. */
export function joinDescribedBy(
  ...ids: Array<string | undefined | null | false>
): string | undefined {
  const joined = ids.filter(Boolean).join(" ");
  return joined || undefined;
}

/**
 * After a submit attempt, move focus to the first field marked
 * aria-invalid inside `scope` (WCAG 3.3.1). Runs after the next paint so
 * error state set by the click handler has rendered. Leaves focus alone if
 * the click already moved it (e.g. a dialog opened) or nothing is invalid.
 */
export function focusFirstInvalid(scope: HTMLElement | null, trigger?: HTMLElement | null) {
  if (!scope) return;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      if (trigger && document.activeElement !== trigger) return;
      const invalid = scope.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (!invalid) return;
      invalid.focus();
      invalid.scrollIntoView?.({ block: "center" });
    })
  );
}
